import React, { useMemo, useState } from 'react';
import { Appointment, Dentist, Patient } from '../../types';
import { Icons } from '../Icons';

interface FollowUpViewProps {
  patients: Patient[];
  appointments: Appointment[];
  dentists: Dentist[];
  onOpenPatient?: (patientId: string) => void;
  onSendReminder?: (patientId: string) => void;
}

type RecallType = 'Post-procedure check' | 'Hygiene recall' | 'Ortho adjustment';

const HIGH_RISK_KEYWORDS = ['periodontal', 'perio', 'smoker', 'smoking', 'diabetic', 'diabetes'];

const parseDate = (dateStr: string) => new Date(`${dateStr}T00:00:00Z`);
const addDays = (dateStr: string, days: number) => {
  const d = parseDate(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split('T')[0];
};
const daysUntil = (dateStr: string) => {
  const target = parseDate(dateStr);
  const today = parseDate(new Date().toISOString().split('T')[0]);
  const diff = target.getTime() - today.getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

const recallTypeFromAppt = (apptType?: string): RecallType => {
  if (!apptType) return 'Hygiene recall';
  const lower = apptType.toLowerCase();
  if (lower.includes('ortho') || lower.includes('align') || lower.includes('brace')) {
    return 'Ortho adjustment';
  }
  if (
    lower.includes('extraction') ||
    lower.includes('implant') ||
    lower.includes('crown') ||
    lower.includes('root') ||
    lower.includes('filling') ||
    lower.includes('surgery')
  ) {
    return 'Post-procedure check';
  }
  return 'Hygiene recall';
};

export const FollowUpView: React.FC<FollowUpViewProps> = ({
  patients,
  appointments,
  dentists,
  onOpenPatient,
  onSendReminder,
}) => {
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState<'all' | 'high' | 'normal'>('all');

  const rows = useMemo(() => {
    return patients.map(patient => {
      const patientAppts = appointments
        .filter(a => a.patientId === patient.id && a.status !== 'CANCELLED' && a.status !== 'NO_SHOW')
        .sort((a, b) => (a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date)))
        .reverse();

      const lastAppt = patientAppts[0];
      const baseDate = lastAppt?.date || patient.lastVisit || new Date().toISOString().split('T')[0];
      const recallType = recallTypeFromAppt(lastAppt?.type);

      const textForRisk = `${patient.alerts.join(' ')} ${patient.dentalHistory}`.toLowerCase();
      const riskLevel: 'High risk' | 'Normal' = HIGH_RISK_KEYWORDS.some(k => textForRisk.includes(k))
        ? 'High risk'
        : 'Normal';

      let intervalDays = 30;
      if (recallType === 'Hygiene recall') intervalDays = 180;
      if (recallType === 'Ortho adjustment') intervalDays = 30;
      if (recallType === 'Post-procedure check') intervalDays = 30;

      // High-risk patients get shorter recall cycles
      if (riskLevel === 'High risk') {
        if (recallType === 'Hygiene recall') intervalDays = 120;
        if (recallType === 'Post-procedure check') intervalDays = 21;
      }

      const nextDate = addDays(baseDate, intervalDays);
      const provider = lastAppt ? dentists.find(d => d.id === lastAppt.providerId)?.name : 'Any provider';
      const daysToGo = daysUntil(nextDate);

      let urgency: 'overdue' | 'soon' | 'scheduled' = 'scheduled';
      if (daysToGo < 0) urgency = 'overdue';
      else if (daysToGo <= 14) urgency = 'soon';

      const cadenceLabel =
        recallType === 'Hygiene recall'
          ? riskLevel === 'High risk'
            ? 'Every 4 months'
            : 'Every 6 months'
          : recallType === 'Ortho adjustment'
          ? 'Monthly'
          : riskLevel === 'High risk'
          ? 'Every 3 weeks'
          : 'Monthly';

      return {
        patient,
        recallType,
        riskLevel,
        nextDate,
        lastAppt,
        provider,
        daysToGo,
        urgency,
        cadenceLabel,
      };
    });
  }, [patients, appointments, dentists]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(row => {
      if (riskFilter === 'high' && row.riskLevel !== 'High risk') return false;
      if (riskFilter === 'normal' && row.riskLevel !== 'Normal') return false;
      if (!q) return true;
      const haystack = `${row.patient.name} ${row.recallType} ${row.patient.email}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search, riskFilter]);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black">Follow-Up & Recall Intelligence</h2>
          <p className="text-sm text-slate-500">
            Auto-calculated next visits by recall type and patient risk profile.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search patients or recall type"
            className="border rounded-xl px-3 py-2 text-sm w-full sm:w-64"
          />
          <select
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value as 'all' | 'high' | 'normal')}
            className="border rounded-xl px-3 py-2 text-sm w-full sm:w-44"
          >
            <option value="all">All risk levels</option>
            <option value="high">High risk only</option>
            <option value="normal">Normal risk only</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredRows.map(row => (
          <div
            key={row.patient.id}
            className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col gap-3 animate-scaleIn"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs text-slate-400 uppercase font-bold">Patient</p>
                <p className="text-lg font-black">{row.patient.name}</p>
                <p className="text-xs text-slate-500">{row.patient.email}</p>
              </div>
              <div
                className={`px-3 py-1 rounded-xl text-[11px] font-bold ${
                  row.riskLevel === 'High risk' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                {row.riskLevel}
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm font-bold">
              <Icons.Calendar />
              <span>Recall: {row.recallType}</span>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Icons.Clock />
                <span className="font-semibold">Next follow-up:</span>
                <span
                  className={`font-bold ${
                    row.urgency === 'overdue'
                      ? 'text-rose-700'
                      : row.urgency === 'soon'
                      ? 'text-amber-600'
                      : 'text-slate-800'
                  }`}
                >
                  {row.nextDate}
                </span>
                <span className="text-xs text-slate-500">({row.daysToGo} days)</span>
              </div>
              <p className="text-xs text-slate-500">Cadence: {row.cadenceLabel}</p>
            </div>

            <div className="text-xs text-slate-500 space-y-1">
              <p>Last visit: {row.lastAppt ? `${row.lastAppt.date} - ${row.lastAppt.type}` : 'None recorded'}</p>
              <p>Primary provider: {row.provider}</p>
            </div>

            {onOpenPatient && onSendReminder && (
              <div className="flex items-center gap-2">
                <button
                  className="h-9 px-3 inline-flex items-center gap-2 justify-center rounded-lg border text-slate-600 hover:bg-slate-50 text-xs font-bold"
                  onClick={() => onOpenPatient?.(row.patient.id)}
                  title="Open patient"
                  aria-label="Open patient"
                >
                  <Icons.FileText />
                  <span>Patient</span>
                </button>
                <button
                  className="h-9 px-3 inline-flex items-center gap-2 justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-xs font-bold"
                  onClick={() => onSendReminder?.(row.patient.id)}
                  title="Send notification"
                  aria-label="Send notification"
                >
                  <Icons.Bell />
                  <span>Notify</span>
                </button>
              </div>
            )}

          </div>
        ))}
      </div>
    </div>
  );
};


